import React, { useState, useEffect } from 'react';
import { AdminThemeProvider, useAdminTheme } from '../contexts/AdminThemeContext';
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { 
  LayoutDashboardIcon, 
  FileTextIcon, 
  BookOpenIcon, 
  GraduationCapIcon,
  VideoIcon,
  UsersIcon,
  UserCheckIcon,
  TrendingUpIcon,
  SettingsIcon,
  CrownIcon,
  BuildingIcon,
  LayersIcon,
  GlobeIcon
} from "lucide-react";
import Icon from './AppIcon';
import blogService from '../services/blogService';
import userService from '../services/userService';
import supabase from '../lib/supabaseClient';
import ResourcesManagement from './ResourcesManagement';
import EnhancedResourcesManagement from './EnhancedResourcesManagement';
import LearningPathsManagement from './LearningPathsManagement';
import WebinarManagement from './WebinarManagement';
import UserManagement from './UserManagement';
import UserApprovals from './UserApprovals';
import Analytics from './Analytics';
import Settings from './Settings';
import LeadershipManagement from './LeadershipManagement';
import AboutContentManagement from './AboutContentManagement';
import VisualContentEditor from './VisualContentEditor';
// Import new enhanced components
import { EnhancedSectionCards } from './admin/EnhancedSectionCards';
import { EnhancedChartArea } from './admin/EnhancedChartArea';
import { EnhancedDataTable } from './admin/EnhancedDataTable';

// Custom sidebar data for BootMinds
const bootmindsData = {
  user: {
    name: "Admin User",
    email: "admin@bootminds.com",
    avatar: "/favicon/favicon-64x64.png",
  },
  navMain: [
    {
      title: "Dashboard",
      url: "#",
      icon: LayoutDashboardIcon,
      isActive: true,
    },
    {
      title: "Resources & Insights Hub",
      url: "#",
      icon: LayersIcon,
      items: [
        {
          title: "Blog Management",
          url: "#",
          icon: FileTextIcon,
        },
        {
          title: "Resources Hub",
          url: "#",
          icon: BookOpenIcon,
        },
        {
          title: "Learning Paths",
          url: "#",
          icon: GraduationCapIcon,
        },
        {
          title: "Webinar Library",
          url: "#",
          icon: VideoIcon,
        },
      ],
    },
    {
      title: "Website Management",
      url: "#",
      icon: GlobeIcon,
      items: [
        {
          title: "Visual Content Editor",
          url: "#",
          icon: LayersIcon,
        },
        {
          title: "Leadership & Advisory",
          url: "#",
          icon: CrownIcon,
        },
        {
          title: "About Us Content",
          url: "#",
          icon: BuildingIcon,
        },
      ],
    },
    {
      title: "User Management",
      url: "#",
      icon: UsersIcon,
    },
    {
      title: "User Approvals",
      url: "#",
      icon: UserCheckIcon,
    },
  ],
  navSecondary: [
    {
      title: "Analytics",
      url: "#",
      icon: TrendingUpIcon,
    },
    {
      title: "Settings",
      url: "#",
      icon: SettingsIcon,
    },
  ],
  documents: [
    {
      name: "Content Library",
      url: "#",
      icon: FileTextIcon,
    },
    {
      name: "Analytics Reports",
      url: "#",
      icon: TrendingUpIcon,
    },
    {
      name: "User Data",
      url: "#",
      icon: UsersIcon,
    },
  ],
};





// Professional Dashboard Overview Component
const ProfessionalDashboardOverview = ({ currentColors }) => {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: currentColors.text }}>
            Dashboard Overview
          </h1>
          <p className="text-sm" style={{ color: currentColors.textMuted }}>
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" 
             style={{ backgroundColor: `${currentColors.success}10`, borderColor: `${currentColors.success}30`, color: currentColors.success }}>
          <Icon name="TrendingUp" size={14} />
          <span className="text-sm font-medium">All systems operational</span>
        </div>
      </div>

      {/* Enhanced Section Cards */}
      <EnhancedSectionCards />
      
      {/* Charts Section */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <EnhancedChartArea />
        </div>
        <div className="space-y-4">
          {/* Quick Stats */}
          <div 
            className="p-4 rounded-lg border"
            style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: currentColors.text }}>
              Quick Stats
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: currentColors.textMuted }}>
                  Active Sessions
                </span>
                <span className="text-sm font-medium" style={{ color: currentColors.text }}>
                  245
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: currentColors.textMuted }}>
                  Bounce Rate
                </span>
                <span className="text-sm font-medium" style={{ color: currentColors.text }}>
                  32.5%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: currentColors.textMuted }}>
                  Page Views
                </span>
                <span className="text-sm font-medium" style={{ color: currentColors.text }}>
                  1,235
                </span>
              </div>
            </div>
          </div>
          
          {/* Recent Activity */}
          <div 
            className="p-4 rounded-lg border"
            style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: currentColors.text }}>
              Recent Activity
            </h3>
            <div className="space-y-3">
              {[
                { action: 'New blog post published', time: '2m ago', icon: 'FileText', color: currentColors.success },
                { action: 'User registered', time: '15m ago', icon: 'UserPlus', color: currentColors.primary },
                { action: 'System backup completed', time: '1h ago', icon: 'Shield', color: currentColors.info }
              ].map((activity, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${activity.color}20` }}
                  >
                    <Icon name={activity.icon} size={12} style={{ color: activity.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: currentColors.text }}>
                      {activity.action}
                    </p>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Data Table */}
      <EnhancedDataTable />
    </div>
  );
};

// Blog Management Component
const BlogManagement = ({ currentColors }) => {
  const [blogs, setBlogs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBlog, setNewBlog] = useState({ title: '', content: '', useAI: false, topic: '' });
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error loading blogs:', error);
      const savedBlogs = blogService.getBlogs();
      setBlogs(savedBlogs);
    }
  };

  const generateWithAI = async () => {
    setIsGenerating(true);
    try {
      const content = await blogService.generateBlogContent(newBlog.title, newBlog.topic);
      setNewBlog(prev => ({ ...prev, content }));
    } catch (error) {
      console.error('Error generating content:', error);
      setNewBlog(prev => ({
        ...prev,
        content: `# ${prev.title}

## Introduction
This is a professional blog post about ${prev.title.toLowerCase()}. 

*Note: To enable AI content generation, please add your OpenAI API key to the environment variables as VITE_OPENAI_API_KEY.*

## Key Topics
- Industry insights and best practices
- Strategic recommendations
- Implementation guidelines
- Future considerations

## Conclusion
This content provides valuable insights for business leaders and technology professionals.`
      }));
    }
    setIsGenerating(false);
  };

  const saveBlog = async () => {
    try {
      const blogData = {
        title: newBlog.title,
        slug: newBlog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        content: newBlog.content,
        excerpt: newBlog.content.substring(0, 150) + '...',
        category: newBlog.topic,
        status: 'draft',
        author_name: 'Admin',
        author_role: 'Content Manager',
        read_time_minutes: Math.ceil(newBlog.content.length / 200)
      };
      
      const { error } = await supabase
        .from('blogs')
        .insert([blogData]);

      if (error) throw error;
      
      await loadBlogs();
      setNewBlog({ title: '', content: '', useAI: false, topic: '' });
      setShowCreateModal(false);
      alert('Blog saved successfully!');
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog: ' + error.message);
    }
  };

  const deleteBlog = (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        blogService.deleteBlog(blogId);
        loadBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Error deleting blog. Please try again.');
      }
    }
  };

  const publishBlog = (blogId) => {
    try {
      blogService.publishBlog(blogId);
      loadBlogs();
      alert('Blog published successfully!');
    } catch (error) {
      console.error('Error publishing blog:', error);
      alert('Error publishing blog. Please try again.');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Blog Management
          </h2>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            Create, manage, and publish your blog content
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 flex items-center gap-2"
          style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
        >
          <Icon name="Plus" size={16} />
          Create New Blog
        </button>
      </div>

      {/* Blog List */}
      <div 
        className="rounded-xl shadow-sm border overflow-hidden"
        style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: currentColors.background }}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Title
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Author
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Date
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog) => (
                <tr key={blog.id} className="border-t" style={{ borderColor: currentColors.border }}>
                  <td className="px-6 py-4">
                    <p className="font-medium" style={{ color: currentColors.text }}>
                      {blog.title}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: blog.status === 'published' ? `${currentColors.success}20` : `${currentColors.warning}20`,
                        color: blog.status === 'published' ? currentColors.success : currentColors.warning
                      }}
                    >
                      {blog.status}
                    </span>
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {blog.author}
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {blog.status === 'draft' && (
                        <button 
                          onClick={() => publishBlog(blog.id)}
                          className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                          title="Publish"
                        >
                          <Icon name="Send" size={16} style={{ color: currentColors.success }} />
                        </button>
                      )}
                      <button 
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Edit"
                      >
                        <Icon name="Edit" size={16} style={{ color: currentColors.primary }} />
                      </button>
                      <button 
                        onClick={() => deleteBlog(blog.id)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} style={{ color: currentColors.error }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Blog Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Create New Blog Post
              </h3>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Blog Title
                </label>
                <input
                  type="text"
                  value={newBlog.title}
                  onChange={(e) => setNewBlog(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: currentColors.background, 
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Enter blog title..."
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="useAI"
                  checked={newBlog.useAI}
                  onChange={(e) => setNewBlog(prev => ({ ...prev, useAI: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="useAI" className="text-sm" style={{ color: currentColors.text }}>
                  Generate content with AI (OpenAI GPT)
                </label>
              </div>

              {newBlog.useAI && (
                <button
                  onClick={generateWithAI}
                  disabled={!newBlog.title || isGenerating}
                  className="w-full px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: currentColors.accent, color: '#ffffff' }}
                >
                  {isGenerating ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <Icon name="Zap" size={16} />
                      Generate Content with AI
                    </>
                  )}
                </button>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Content
                </label>
                <textarea
                  value={newBlog.content}
                  onChange={(e) => setNewBlog(prev => ({ ...prev, content: e.target.value }))}
                  rows={12}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: currentColors.background, 
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Write your blog content here..."
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  style={{ backgroundColor: currentColors.border, color: currentColors.text }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveBlog}
                  disabled={!newBlog.title}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50"
                  style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
                >
                  Save Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Navigation handler
const NavigationHandler = ({ activeTab, setActiveTab, item, isChild = false }) => {
  const handleClick = (e) => {
    e.preventDefault();
    
    // Map navigation items to activeTab values
    const navigationMap = {
      'Dashboard': 'dashboard',
      'Blog Management': 'blogs',
      'Resources Hub': 'resources',
      'Learning Paths': 'learning-paths',
      'Webinar Library': 'webinars',
      'Webinar Analytics': 'webinar-analytics',
      'Leadership & Advisory': 'leadership',
      'About Us Content': 'about-content',
      'User Management': 'users',
      'User Approvals': 'approvals',
      'Analytics': 'analytics',
      'Settings': 'settings'
    };
    
    const tabKey = navigationMap[item.title];
    if (tabKey) {
      setActiveTab(tabKey);
    }
  };

  return (
    <button onClick={handleClick} className="w-full text-left">
      {item.title}
    </button>
  );
};

// Main Enhanced Admin Dashboard Component
const EnhancedAdminDashboardContent = ({ onLogout }) => {
  console.log('🎯 EnhancedAdminDashboard: Component mounted/rendered');

  const { currentColors } = useAdminTheme();
  const [activeTab, setActiveTab] = useState('dashboard');

  console.log('🎯 EnhancedAdminDashboard: Initial activeTab:', activeTab);

  const renderContent = () => {
    console.log('🎯 EnhancedAdminDashboard: renderContent called with activeTab:', activeTab);
    switch (activeTab) {
      case 'dashboard':
        return <ProfessionalDashboardOverview currentColors={currentColors} />;
      case 'blogs':
        return <BlogManagement currentColors={currentColors} />;
      case 'resources':
        return <EnhancedResourcesManagement />;
      case 'learning-paths':
        return <LearningPathsManagement />;
      case 'webinars':
        console.log('🎬 EnhancedAdminDashboard: Rendering WebinarManagement');
        return <WebinarManagement />;
      case 'approvals':
        return <UserApprovals />;
      case 'users':
        return <UserManagement />;
      case 'analytics':
        return <Analytics />;
      case 'analytics-reports':
        return <Analytics />;
      case 'leadership':
        return <LeadershipManagement />;
      case 'about-content':
        return <AboutContentManagement />;
      case 'visual-editor':
        return <VisualContentEditor />;
      case 'settings':
        return <Settings />;
      default:
        return <ProfessionalDashboardOverview currentColors={currentColors} />;
    }
  };

  // Create custom sidebar with your navigation
  const customSidebarData = {
    ...bootmindsData,
    navMain: bootmindsData.navMain.map(item => ({
      ...item,
      onClick: () => {
        if (item.title === 'Dashboard') setActiveTab('dashboard');
        if (item.title === 'User Management') setActiveTab('users');
        if (item.title === 'User Approvals') setActiveTab('approvals');
      },
      items: item.items?.map(subItem => ({
        ...subItem,
        onClick: () => {
          if (subItem.title === 'Blog Management') setActiveTab('blogs');
          if (subItem.title === 'Resources Hub') setActiveTab('resources');
          if (subItem.title === 'Learning Paths') setActiveTab('learning-paths');
          if (subItem.title === 'Webinar Library') setActiveTab('webinars');
          if (subItem.title === 'Visual Content Editor') setActiveTab('visual-editor');
          if (subItem.title === 'Leadership & Advisory') setActiveTab('leadership');
          if (subItem.title === 'About Us Content') setActiveTab('about-content');
        }
      }))
    })),
    navSecondary: bootmindsData.navSecondary.map(item => ({
      ...item,
      onClick: () => {
        if (item.title === 'Analytics') setActiveTab('analytics');
        if (item.title === 'Settings') setActiveTab('settings');
      }
    }))
  };

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "288px",
        "--header-height": "0px",
      }}
    >
      <AppSidebar data={customSidebarData} setActiveTab={setActiveTab} onLogout={onLogout} variant="inset" data-testid="admin-sidebar" />
      <div className="flex flex-1 flex-col min-w-0" style={{ backgroundColor: currentColors.background }} data-testid="admin-main">
        <main className="flex-1 overflow-y-auto" data-testid="admin-content">
          <div className="@container/main flex flex-1 flex-col">
            {renderContent()}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

// Wrapper component that provides theme context
const EnhancedAdminDashboard = ({ onLogout }) => {
  return (
    <AdminThemeProvider>
      <EnhancedAdminDashboardContent onLogout={onLogout} />
    </AdminThemeProvider>
  );
};

export default EnhancedAdminDashboard;
